import { 
  doc, 
  getDoc, 
  collection, 
  getDocs, 
  updateDoc,
  setDoc, 
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  writeBatch,
  where
} from 'firebase/firestore';
import { db } from './firebase';

// Obtener información de la rifa
export const getRaffle = async (raffleId) => {
  try {
    const raffleDoc = await getDoc(doc(db, 'raffles', raffleId));
    if (raffleDoc.exists()) {
      const raffleData = { id: raffleDoc.id, ...raffleDoc.data() };
      console.log('Raffle data from Firestore:', raffleData);
      console.log('Image URL:', raffleData.imageUrl);
      return raffleData;
    }
    return null;
  } catch (error) {
    console.error('Error getting raffle:', error);
    throw error;
  }
};

// Obtener todos los tickets de una rifa
export const getTickets = async (raffleId) => {
  try {
    const ticketsRef = collection(db, 'raffles', raffleId, 'tickets');
    const q = query(ticketsRef, orderBy('number'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting tickets:', error);
    throw error;
  }
};

// Escuchar cambios en tiempo real de los tickets
export const subscribeToTickets = (raffleId, callback) => {
  const ticketsRef = collection(db, 'raffles', raffleId, 'tickets');
  const q = query(ticketsRef, orderBy('number'));
  
  return onSnapshot(q, (snapshot) => {
    const tickets = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(tickets);
  });
};

// Reservar un ticket
export const reserveTicket = async (raffleId, ticketNumber, buyerData) => {
  try {
    console.log('=== DEBUG reserveTicket ===');
    console.log('raffleId:', raffleId);
    console.log('ticketNumber:', ticketNumber);
    console.log('buyerData completo:', buyerData);
    console.log('specialNote:', buyerData.specialNote);
    console.log('========================');
    
    const ticketRef = doc(db, 'raffles', raffleId, 'tickets', ticketNumber.toString());
    
    // Preparar los datos a guardar
    const dataToSave = {
      // Campos principales con diferentes formatos para compatibilidad
      number: ticketNumber.toString(),
      displayNumber: ticketNumber.toString(),
      ticketNumber: parseInt(ticketNumber), // Como número para Android
      
      // Estados del ticket - múltiples formatos para compatibilidad
      reserved: true,
      isReserved: true,  // Formato alternativo para Android
      sold: false,  // Explícitamente marcar como no vendido
      isSold: false, // Por compatibilidad con Android
      status: 'reserved', // Campo de estado textual
      ticketStatus: 'RESERVED', // Formato en mayúsculas
      
      // Información del comprador
      buyerName: buyerData.name,
      buyerPhone: buyerData.phone,
      buyerId: buyerData.id || generateBuyerId(),
      
      // Timestamps en múltiples formatos
      reservedAt: serverTimestamp(),
      reservedDate: serverTimestamp(), // Formato alternativo
      lastUpdated: serverTimestamp(),
      updatedAt: serverTimestamp(), // Formato alternativo
      
      // Marcadores de origen
      reservedFromWeb: true,  // Marcar que la reserva viene de la web
      platform: 'web',
      source: 'web',
      
      // Campos adicionales
      email: buyerData.email || '',
      address: buyerData.address || '',
      annotation: buyerData.specialNote || '',  // Campo de anotación especial
      totalPaid: buyerData.totalPaid || 0,
      paymentHistory: buyerData.paymentHistory || []
    };
    
    console.log('=== Datos que se enviarán a Firestore ===');
    console.log(JSON.stringify(dataToSave, null, 2));
    console.log('=======================================');
    
    // Usar setDoc con merge para crear el documento si no existe
    await setDoc(ticketRef, dataToSave, { merge: true });
    
    console.log('=== Datos guardados en Firestore ===');
    console.log('Campo annotation guardado como:', buyerData.specialNote || '(vacío)');
    console.log('==================================');
    
    // Verificar inmediatamente que se guardó
    const verifyDoc = await getDoc(ticketRef);
    if (verifyDoc.exists()) {
      const savedData = verifyDoc.data();
      console.log('=== VERIFICACIÓN: Datos realmente guardados ===');
      console.log('reserved:', savedData.reserved);
      console.log('isReserved:', savedData.isReserved);
      console.log('buyerName:', savedData.buyerName);
      console.log('Document path:', `raffles/${raffleId}/tickets/${ticketNumber}`);
      console.log('===============================================');
    } else {
      console.error('ERROR: El documento no se creó en Firebase!');
    }
    
    return true;
  } catch (error) {
    console.error('Error reserving ticket:', error);
    throw error;
  }
};

// Generar ID único para el comprador
const generateBuyerId = () => {
  return 'buyer_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

// Reservar múltiples tickets
export const reserveMultipleTickets = async (raffleId, tickets, buyerData) => {
  try {
    const promises = tickets.map(ticket => 
      reserveTicket(raffleId, ticket.number, buyerData)
    );
    
    await Promise.all(promises);
    return true;
  } catch (error) {
    console.error('Error reserving multiple tickets:', error);
    throw error;
  }
};

// Formatear precio en moneda local
export const formatPrice = (price) => {
  const numPrice = parseFloat(price.replace(/[^0-9.-]+/g, ''));
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN'
  }).format(numPrice);
};

// Forzar sincronización con Android
export const forceSyncWithAndroid = async (raffleId) => {
  try {
    console.log('=== FORZANDO SINCRONIZACIÓN CON ANDROID ===');
    
    // Crear un documento de señal para Android
    const syncSignalRef = doc(db, 'raffles', raffleId, 'sync', 'signal');
    await setDoc(syncSignalRef, {
      timestamp: serverTimestamp(),
      action: 'SYNC_TICKETS',
      source: 'web',
      requestedAt: new Date().toISOString()
    });
    
    console.log('Señal de sincronización enviada');
    
    // También actualizar metadata de la rifa
    const raffleRef = doc(db, 'raffles', raffleId);
    await setDoc(raffleRef, {
      lastSync: serverTimestamp(),
      needsSync: true
    }, { merge: true });
    
    console.log('Metadata de rifa actualizada');
    console.log('==========================================');
    
    return true;
  } catch (error) {
    console.error('Error forcing sync:', error);
    throw error;
  }
};

// Función de debug para verificar la sincronización con Android
export const debugTicketSync = async (raffleId, ticketNumber) => {
  try {
    const ticketRef = doc(db, 'raffles', raffleId, 'tickets', ticketNumber.toString());
    const ticketDoc = await getDoc(ticketRef);
    
    if (ticketDoc.exists()) {
      const data = ticketDoc.data();
      console.log('=== TICKET DEBUG INFO ===');
      console.log('Ticket Number:', ticketNumber);
      console.log('Document ID:', ticketDoc.id);
      console.log('Full Data:', JSON.stringify(data, null, 2));
      console.log('Reserved fields:', {
        reserved: data.reserved,
        isReserved: data.isReserved,
        status: data.status,
        ticketStatus: data.ticketStatus
      });
      console.log('Sold fields:', {
        sold: data.sold,
        isSold: data.isSold
      });
      console.log('Platform info:', {
        reservedFromWeb: data.reservedFromWeb,
        platform: data.platform,
        source: data.source
      });
      console.log('========================');
      return data;
    } else {
      console.log('Ticket not found in database');
      return null;
    }
  } catch (error) {
    console.error('Error debugging ticket:', error);
    throw error;
  }
};

// Función para migrar tickets existentes al nuevo formato
export const migrateTicketsToNewFormat = async (raffleId) => {
  try {
    console.log('Starting ticket migration for raffle:', raffleId);
    
    const ticketsRef = collection(db, 'raffles', raffleId, 'tickets');
    const q = query(ticketsRef);
    const snapshot = await getDocs(q);
    
    const batch = writeBatch(db);
    let migratedCount = 0;
    
    snapshot.docs.forEach(ticketDoc => {
      const data = ticketDoc.data();
      const updates = {};
      
      // Solo migrar tickets que estén reservados o vendidos
      if (data.reserved || data.sold) {
        // Agregar campos faltantes para compatibilidad con Android
        if (data.reserved && !data.isReserved) {
          updates.isReserved = true;
        }
        
        if (data.sold && !data.isSold) {
          updates.isSold = true;
        }
        
        // Agregar campo de estado textual
        if (!data.status) {
          if (data.sold) {
            updates.status = 'sold';
            updates.ticketStatus = 'SOLD';
          } else if (data.reserved) {
            updates.status = 'reserved';
            updates.ticketStatus = 'RESERVED';
          }
        }
        
        // Agregar número como entero si no existe
        if (!data.ticketNumber && data.number) {
          updates.ticketNumber = parseInt(data.number);
        }
        
        // Agregar timestamps alternativos
        if (data.reservedAt && !data.reservedDate) {
          updates.reservedDate = data.reservedAt;
        }
        
        if (data.lastUpdated && !data.updatedAt) {
          updates.updatedAt = data.lastUpdated;
        }
        
        // Agregar información de plataforma si no existe
        if (!data.platform && data.reservedFromWeb) {
          updates.platform = 'web';
          updates.source = 'web';
        }
        
        if (Object.keys(updates).length > 0) {
          batch.update(ticketDoc.ref, {
            ...updates,
            lastUpdated: serverTimestamp()
          });
          migratedCount++;
        }
      }
    });
    
    if (migratedCount > 0) {
      await batch.commit();
      console.log(`Migration completed. Updated ${migratedCount} tickets.`);
    } else {
      console.log('No tickets needed migration.');
    }
    
    return { success: true, migratedCount };
  } catch (error) {
    console.error('Error migrating tickets:', error);
    throw error;
  }
};